/**
 * Store pour la liste des images destinées à l'analyse par l'IA
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useUserStore } from '@stores/user';

export const useAuthAIPostImageStore = defineStore('authAIPostImage', (() => {

    const userStore = useUserStore();

    // State des images chargées
    const postImages = ref([]);

    // Données de la liste (images et total
    const postImageList = ref({});

    // Retourne l'utilisateur que l'on gère
    const getManagingUser = computed(() => userStore.getCurrentManagingUser);

    /**
     * Retourne les données de la liste (images et total)
     * @return object
     */
    const getList = computed(() => postImageList.value || {});

    /**
     * Chargement de la liste des images
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadPostImageList = ((params) => {

        const userId = getManagingUser.value.id;
        const path = `/api/users/${ userId }/ai/posts-images`;

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {
                if(params.offset === 0) {
                    resetPostImageList();
                }

                updatePostImageList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Suppression de l'image en paramètre
     * @param object postImage
     * @return Promise
     */
    const remove = ((postImage) => {

        const userId = getManagingUser.value.id;
        const path = `/api/users/${ userId }/ai/posts-images/${ postImage.id }`;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemove(postImage);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Méthode appelée après la suppression d'une image
     * @param object image
     * @return void
     */
    const onRemove = ((image) => {

        // Mise à jour dans les images chargées
        const indexToDelete = postImages.value.findIndex((itemFromState) => itemFromState.id === image.id);
        if(indexToDelete !== -1) {
            postImages.value.splice(indexToDelete, 1);
        }

        // Mise à jour dans la liste
        const currentListData = postImageList.value.images || [];
        const indexFromList = currentListData.findIndex((itemFromState) => itemFromState.id === image.id);
        if(indexFromList !== -1) {
            postImageList.value.images.splice(indexFromList, 1);
            postImageList.value.total = Math.max(0, postImageList.value.total - 1);
        }

    });

    /**
     * Modifie la liste des images dans le store
     * @param data Données des images : total et items
     * @return void
     */
    const updatePostImageList = ((data) => {

        const currentData = postImageList.value;
        if(currentData.total === undefined) {
            postImageList.value = {
                total: data.total,
                images: data.items
            };

            data.items.forEach((image) => {
                updatePostImage(image);
            });

        } else {
            postImageList.value.total = data.total;

            const currentImages = postImageList.value.images;
            const newImages = data.items.filter((item) => ! currentImages.find((current) => current.id === item.id));
            postImageList.value.images = [ ...currentImages, ...newImages ];

            newImages.forEach((image) => {
                updatePostImage(image);
            });
        }

    });

    /**
     * Modifie une image dans le store
     * @param object image Données de l'image à mettre à jour dans le store
     * @return Promise
     */
    const updatePostImage = ((image) => {

        const currentImages = postImages.value;

        const index = currentImages.findIndex((imageFromState) => imageFromState.id === image.id);
        if(index !== -1) {
            postImages.value[index] = { ...postImages.value[index], ...image };
        } else {
            postImages.value.push(image);
        }

        // Mise à jour dans la liste
        const currentListData = postImageList.value.images || [];
        const indexFromList = currentListData.findIndex((imageFromState) => imageFromState.id === image.id);
        if(indexFromList !== -1) {
            const currentImageFromList = postImageList.value.images[indexFromList];
            postImageList.value.images[indexFromList] = { ...currentImageFromList, ...image };
        } else if(postImageList.value.total !== undefined) {
            postImageList.value.images ??= [];
            postImageList.value.images.push(image);
            postImageList.value.total += 1;
        }

    });

    /**
     * Réinitialisation des images dans le store
     * @return object
     */
    const resetPostImageList = (() => {
        postImageList.value = {};
        postImages.value = [];
    });

    return {
        getList,
        loadPostImageList,
        updatePostImage,
        remove,
        resetPostImageList
    };

}));


